
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { authRateLimiter, formatTimeUntilReset } from '@/utils/rateLimiter';
import { isValidEmail, validatePasswordStrength, sanitizeText } from '@/utils/validation';

export interface UseSignUpReturn {
  signUp: (email: string, password: string) => Promise<{ success: boolean; requiresEmailVerification?: boolean }>;
  validatePassword: (password: string) => { valid: boolean; errors: string[] };
  isLoading: boolean;
}

export const useSignUp = (): UseSignUpReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const validatePassword = (password: string) => {
    return validatePasswordStrength(password);
  };

  const signUp = async (email: string, password: string) => {
    try {
      console.log("Attempting sign up for:", email);
      setIsLoading(true);
      
      // Quick client-side validation before making API calls
      if (!email || !password) {
        toast({
          title: 'Missing information',
          description: 'Please provide both email and password',
          variant: 'destructive',
        });
        return { success: false };
      }

      if (!isValidEmail(email)) {
        toast({
          title: 'Invalid email',
          description: 'Please provide a valid email address',
          variant: 'destructive',
        });
        return { success: false };
      }

      const sanitizedEmail = sanitizeText(email);
      
      // Rate limiting check
      const rateLimitKey = `signup:${sanitizedEmail.toLowerCase()}`;
      const rateLimitResult = authRateLimiter.check(rateLimitKey);
      
      if (!rateLimitResult.allowed) {
        const timeUntilReset = formatTimeUntilReset(rateLimitResult.resetAt || 0);
        toast({
          title: 'Too many sign-up attempts',
          description: `Please try again after ${timeUntilReset}`,
          variant: 'destructive',
        });
        return { success: false };
      }

      // Password validation
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        toast({
          title: 'Password too weak',
          description: passwordValidation.errors.join('. '),
          variant: 'destructive',
        });
        return { success: false };
      }

      // Optimized signup - use minimal options
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin + '/login',
        }
      });

      if (error) {
        console.error("Sign up error:", error);
        
        if (error.message.includes('already registered')) {
          toast({
            title: 'Registration failed',
            description: 'This email is already registered. Please try logging in instead.',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Error signing up',
            description: error.message,
            variant: 'destructive',
          });
        }
        return { success: false };
      } else if (data?.user?.identities?.length === 0) {
        toast({
          title: 'Account already exists',
          description: 'An account with this email already exists. Please try to log in.',
          variant: 'destructive',
        });
        return { success: false };
      } else {
        console.log("Sign up successful:", data.user?.id);
        
        // Reset rate limiter
        authRateLimiter.reset(rateLimitKey);
        
        toast({
          title: 'Account created successfully',
          description: 'Please check your email to confirm your account.',
        });
        
        // Navigate immediately for better UX
        setTimeout(() => navigate('/login'), 500);
        return { success: true };
      }
    } catch (error) {
      console.error('Unexpected error during sign up:', error);
      toast({
        title: 'Registration failed',
        description: 'An unexpected error occurred. Please try again later.',
        variant: 'destructive',
      });
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signUp,
    validatePassword,
    isLoading
  };
};
